import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Search, X } from 'react-feather';
import { ParagraphNode, TextNode } from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import Fuse from 'fuse.js';
import { Calendar } from 'react-date-range';
import moment from 'moment';
import { useHotkeys } from 'react-hotkeys-hook';
import _ from 'lodash';
import { useSelector } from 'react-redux';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import arrowUpDown from ':assets/images/arrow-up-down.svg';
import arrowLeftRight from ':assets/images/arrow-left-right.svg';
import arrowTurnDown from ':assets/images/arrow-turn-down.svg';
import { colors } from '../ui-elements/Main/MainTheme';
import { SearchPlugin, PrefixNode, searchNodes, ValueNode, type PrefixOption } from '@/lib/lexical/SearchPlugin';
import * as parser from '@/lib/lexical/SearchPlugin/parser';
import { SearchPluginApi, SearchPluginSelection } from '@/lib/lexical/SearchPlugin/SearchPlugin';
import { inboxUsersSelector } from '@/redux/app/selectors/inbox';
import { AppNavigatorParamsContext, useNavigationActions } from '@/lib/navigation';
import { InboxUser } from '@/redux/app/reducers/inbox';
import { useCurrentValue, usePreviousValue } from '@/lib/react-hooks';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { useDisableHotkeys } from '@/hooks/hotkeys';
import { avo } from '@/api/app/avo-client';
import { isPhoneValid } from '@/lib/phone-utils';
import { TicketUnaddressedIcon } from '../ticket/TicketUnaddressedToggle';

export { PrefixOption };

const objectTypes = [
  { value: 'contact.', description: 'Contact fields' },
  { value: 'conversation.', description: 'Conversation fields' },
];
const objectTypesSearch = new Fuse(objectTypes, { keys: ['value', 'description'], includeScore: true, ignoreLocation: true });
const objectFields = [
  { value: 'contact.name' },
  { value: 'contact.email' },
  { value: 'contact.phone' },
  { value: 'contact.company' },
  { value: 'contact.city' },
  { value: 'contact.zip' },
  { value: 'contact.state' },
  { value: 'contact.country' },
  { value: 'conversation.sentiment' },
];
const objectFieldSearch = new Fuse(objectFields, { keys: ['value'], includeScore: true, ignoreLocation: true });

interface ContactFieldVariable {
  name: string;
}

export interface PrefixSearchInputProps {
  subdomain: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  searchAsYouType?: false | 'tickets';
  onClear?: () => void;
  onClose?: () => void; // called when user dismisses floating UI (backdrop click or Escape)
  maxInlineWidth?: number; // when content exceeds this width, element will break out of the page
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  type?: string;
  disableFloating?: boolean;
  dropdownMaxWidth?: string;
  prefixes: PrefixOption[]; // prefix options for this instance (required)
  children?: React.ReactNode; // when children are provided, component is always in floating mode
  z?: number;
}

const Backdrop = styled.div<{ visible: boolean }>`
  display: ${props => props.$visible ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1999;
  backdrop-filter: blur(2px);
`;

const Container = styled.div<{ floating: boolean }>`
  position: relative;
  width: 100%;
  z-index: ${props => props.$floating ? '2001' : '999'};
  
  ${props => props.$floating && `
    min-height: 40px;
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    width: 90%;
    max-width: 800px;
  `}
`;

const editorStyles = (type: string, focused: boolean) => css`
  div[role=textbox] {
    overflow: hidden;
    white-space: pre !important;
    min-width: 5px;
    max-width: 5000px;
    outline: none !important;
  }

  p {
    margin-block-start: unset;
    margin-block-end: unset;
  }

  span {
    margin-right: 4px;
  }

  span.${ValueNode.nodeType}.empty::after {
    content: " ";
  }

  ${type === 'A' ? css`

  ` : ''}

  ${type === 'B' ? css`
    span.${PrefixNode.nodeType} {
      background: #19BE51;
      padding: 2px 6px;
      color: white;
      border-radius: 4px;
      font-size: 14px;
      font-family: Montserrat, sans-serif;
      white-space: nowrap;
    }
    ${focused ? css`
      span.${ValueNode.nodeType}.active {
        text-decoration: underline;
      }
      span.${ValueNode.nodeType}.active.empty::after {
        text-decoration: underline;
      }
    ` : ''}
  ` : ''}

  ${type === 'C' ? css`
    span.${PrefixNode.nodeType} {
      background: #19BE51;
      padding: 2px 6px;
      color: white;
      border-radius: 4px;
      font-size: 14px;
      font-family: Montserrat, sans-serif;
      white-space: nowrap;
    }
    span.${ValueNode.nodeType} {
      text-decoration: underline;
    }
    span.${ValueNode.nodeType}.empty::after {
      text-decoration: underline;
    }
  ` : ''}
`;

const InputContainer = styled.div<{ focused: boolean; floating: boolean, type: string }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px solid ${colors.beige};
  border-radius: 30px;
  background: ${colors.white};
  padding: 11px 35px 9px 35px;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  box-sizing: border-box;
  min-height: 40px;
  transition: all 0.2s ease;

  ${props => props.$focused && `
    border-color: #48937F;
  `}

  ${props => props.$floating && `
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: #48937F;
    z-index: 2;
  `}

  ${({ $type, $focused }) => editorStyles($type, $focused)}
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  
  svg {
    stroke: ${colors.mutedGold};
  }
`;

const ClearButton = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: #D1F2DC;
  padding: 3px;
  height: 24px;
  border-radius: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    stroke: #48937F;
  }
`;

const DropdownWrapper = styled.div<{ visible: boolean; floating: boolean; maxWidth?: string; }>`
  position: ${props => props.$floating ? 'relative' : 'absolute'};
  top: ${props => props.$floating ? '-20px' : '100%'};
  left: 0;
  right: ${props => props.$floating ? '0' : 'auto'};
  ${props => !props.$floating && props.$maxWidth && `
    max-width: ${props.$maxWidth};
    width: max-content;
  `}
  background: white;
  border: 1px solid ${colors.beige};
  border-radius: ${props => props.$floating ? '0 0 20px 20px' : '8px'};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  flex-direction: column;
  margin-top: ${props => props.$floating ? '0' : '4px'};
  overflow: hidden;
  z-index: ${props => props.$floating ? '1' : 'auto'};
  padding-top: ${props => props.$floating ? '28px' : '0'};

  ${props => props.$floating ? css`
    border: none;
    display: flex;
    max-height: ${props.$visible ? '60vh' : '0'};
    opacity: ${props.$visible ? '1' : '0'};
    padding-top: ${props.$visible ? '28px' : '0'};
    transition: max-height 0.3s ease-out, opacity 0.2s ease-out, padding-top 0.3s ease-out;
  ` : css`
    display: ${props.$visible ? 'flex' : 'none'};
  `}
`;

const Dropdown = styled.div<{ floating: boolean; hasHints: boolean }>`
  max-height: ${(props) => props.$floating ? 'calc(50vh - 60px)' : '300px'};
  overflow-y: auto;
  flex: 1;
  ${props => props.$floating && !props.$hasHints && `
    padding-bottom: 8px;
  `}
`;

const DropdownItem = styled.div<{ selected: boolean; layout?: 'vertical' | 'horizontal' }>`
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${props => props.$selected ? '#f5f5f5' : 'transparent'};
  gap: 12px;

  ${props => props.$layout === 'vertical' && css`
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  `}
`;

const PrefixText = styled.span`
  font-weight: 600;
  color: #19BE51;
`;

const DescriptionText = styled.span`
  color: #666;
  font-size: 13px;
`;

const SuggestionContent = styled.span`
  color: #666;
  font-size: 13px;
`;

const ChipContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const PrefixValueChip = styled.span`
  background: #19BE51;
  padding: 2px 6px;
  color: white;
  border-radius: 4px;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  white-space: nowrap;
  margin-right: 4px;
`;

const PlainText = styled.span`
  color: #666;
  font-size: 13px;
  margin-right: 4px;
`;

const Separator = styled.div`
  height: 1px;
  background: ${colors.beige};
  margin: 8px 0;
`;

const KeyboardHints = styled.div<{ floating?: boolean }>`
  padding: 8px;
  background: #f9f9f9;
  border-top: 1px solid ${colors.beige};
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 12px;
  color: #666;
  ${props => props.$floating && `
    border-radius: 0 0 20px 20px;
    padding-left: 12px;
  `}
`;

const KeyboardHint = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const KeyboardKey = styled.kbd`
  background: white;
  border: 1px solid #ddd;
  border-radius: 3px;
  padding: 3px 6px;
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  font-size: 10px;
  font-weight: 600;
  color: #333;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  min-width: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 12px;
    height: 12px;
    fill: #333;
  }
`;

const KeyboardHintText = styled.span`
  color: #666;
`;

const SearchResultItem = styled(DropdownItem)`
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 16px;
`;

const SearchResultLeft = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
  min-width: 0;
`;

const SearchResultRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const StatusBadge = styled.div<{ status: string }>`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background: ${props => {
    switch (props.$status.toLowerCase()) {
      case 'new': return '#E3F2FD';
      case 'open': return '#FFF3E0';
      case 'closed': return '#F5F5F5';
      default: return '#F5F5F5';
    }
  }};
  color: ${props => {
    switch (props.$status.toLowerCase()) {
      case 'new': return '#1976D2';
      case 'open': return '#F57C00';
      case 'closed': return '#757575';
      default: return '#757575';
    }
  }};
`;

const MessagePrefix = styled.span`
  font-weight: 600;
`;

const SearchResultName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const SearchResultCompany = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.darkGreen};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const SearchResultMessage = styled.div`
  font-size: 13px;
  font-weight: 300;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  
  mark {
    background-color: #fff3cd;
    padding: 0 2px;
  }
`;

const PlaceholderText = styled.div`
  position: absolute;
  color: #C6BDAF;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  pointer-events: none;
`;

const DatePickerContainer = styled.div<{ visible: boolean, floating: boolean }>`
  ${(props => props.$visible ? '' : 'display: none;')}
  position: ${props => props.$floating ? 'fixed' : 'absolute'};
  top: ${props => props.$floating ? 'auto' : '100%'};
  left: ${props => props.$floating ? '50%' : '0'};
  right: ${props => props.$floating ? 'auto' : '0'};
  ${props => props.$floating && `
    transform: translateX(-50%);
    width: auto;
  `}
  background: white;
  border: 1px solid ${colors.beige};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
  padding: 8px;
  min-width: 300px;

  .rdrCalendarWrapper {
    background: white;
    font-family: Montserrat, sans-serif;
  }

  .rdrDateDisplayWrapper {
    background: transparent;
  }

  .rdrMonthAndYearWrapper {
    background: white;
    padding-top: 0;
  }

  .rdrMonth {
    width: 100%;
  }

  .rdrMonthPicker select,
  .rdrYearPicker select {
    font-family: Montserrat, sans-serif;
    color: #666;
  }

  .rdrWeekDays {
    font-family: Montserrat, sans-serif;
  }

  .rdrDay {
    font-family: Montserrat, sans-serif;
  }

  .rdrDayNumber span {
    color: #333;
  }

  .rdrDayToday .rdrDayNumber span:after {
    background: #19BE51;
  }

  .rdrSelected,
  .rdrInRange,
  .rdrStartEdge,
  .rdrEndEdge {
    background: #19BE51 !important;
  }

  .rdrDayStartPreview,
  .rdrDayInPreview,
  .rdrDayEndPreview {
    border-color: #19BE51 !important;
  }

  .rdrDefinedRangesWrapper {
    display: none;
  }
`;

const ChildrenContainer = styled.div`
  margin-top: 20px;
`;

interface TicketSearchResult {
  uuid: string;
  unaddressed: boolean;
  status?: string;
  contact: {
    identifier: string;
    display_name: string;
    first_name?: string;
    human_name?: boolean;
    company?: string;
  };
  activity?: {
    match: boolean;
    event_id: number;
    sender_name: string | null;
    message: string;
    sent_at: number;
  };
}

type SuggestionOption = (
  | { type: 'submit', content: string }
  | { type: 'prefix', prefix: string, description: string }
  | { type: 'prefixed-value', prefix: string, value: string, description?: string }
  | { type: 'confirm-value', value: string }
  | { type: 'raw-value', value: string, description?: string, finalize?: boolean }
  | { type: 'user', name: string }
  | { type: 'contact-field', name: string }
  | { type: 'date' }
  | { type: 'ticket', ticket: TicketSearchResult }
);

const getDropdownOptions = (selection: SearchPluginSelection, content: string, userSearch: Fuse<InboxUser>, contactFieldSearch: Fuse<ContactFieldVariable>, prefixOptions: PrefixOption[], prefixSearch: Fuse<PrefixOption>, type?: string): SuggestionOption[] => {
  const submit = { type: 'submit', content, renderType: type } as SuggestionOption;

  // if (!selection) return content.length > 0 ? [submit] : [];
  if (!selection || selection.type === ParagraphNode) return prefixOptions.map((v) => ({ type: 'prefix', ...v })); // empty field
  if (selection.type === PrefixNode) return [submit];
  if (selection.type === TextNode) {
    if (selection.content.trim().length > 0) {
      const prefixMatches = prefixSearch.search(selection.content).filter(({ score }) => score < 0.1);
      const userMatch = userSearch.search(selection.content)[0];
      const phoneOption = PrefixNode.findPrefixOption('phone', prefixOptions);
      const messageOption = PrefixNode.findPrefixOption('message', prefixOptions);
      return ([submit]
        .concat(prefixMatches.map(({ item }) => ({ type: 'prefix', ...item })))
        .concat(isPhoneValid(selection.content) && phoneOption ? [{ type: 'prefixed-value', prefix: 'phone', value: selection.content, description: phoneOption.description }] : [])
        .concat(messageOption ? [{ type: 'prefixed-value', prefix: 'message', value: selection.content, description: messageOption.description }] : [])
        .concat(userMatch && userMatch.score < 0.01 ? [{ type: 'prefixed-value', prefix: 'user', value: userMatch.item.name }] : [])
      );
    }
    const allPrefixes = prefixOptions.map((v) => ({ type: 'prefix', ...v }));
    return content ? [submit].concat(allPrefixes as any) : [submit];
  }
  if (selection.type === ValueNode) {
    const trimmedContent = selection.content.trim();
    // const self = trimmedContent ? { type: 'confirm-value' as const, value: trimmedContent } : null;
    const dataType = PrefixNode.findPrefixOption(selection.siblingContent, prefixOptions)?.dataType;
    if (dataType === 'user') {
      if (selection.content.trim().length < 1) return userSearch.getIndex().toJSON().records.map((v) => v.$[0].v).sort().map((name) => ({ type: 'user', name }));
      let results = userSearch.search(selection.content).filter(({ score }) => score < 0.1).map(({ item }) => ({ type: 'user', name: item.name }));
      results = results.filter(({ name }) => name === trimmedContent);
      return [submit].concat(results as SuggestionOption[]);
    }
    if (dataType === 'date') {
      return [{ type: 'date' }];
    }
    if (dataType === 'field') { //
      if (selection.content.includes('.')) {
        const matches = objectFieldSearch.search(selection.content).filter(({ score }) => score < 0.1);
        return [submit].concat(
          matches.map(({ item }) => ({ type: 'raw-value' as const, ...item })),
        );
      } else if (selection.content.trim().length > 0) {
        const matches = objectTypesSearch.search(selection.content).filter(({ score }) => score < 0.1);
        return [submit].concat(
          matches.map(({ item }) => ({ type: 'raw-value' as const, finalize: false, ...item })),
        );
      }
      return [submit].concat(
        objectTypes.map((item) => ({ type: 'raw-value' as const, finalize: false, ...item })),
      );
    }
    if (dataType === 'contact-field') { //
      if (selection.content.trim().length < 1) {
        return [submit].concat(
          contactFieldSearch.getIndex().toJSON().records.map((v) => v.$[0].v).sort().map((name) => ({ type: 'contact-field', name })),
        );
      }
      let results = contactFieldSearch.search(selection.content).filter(({ score }) => score < 0.1).map(({ item }) => ({ type: 'contact-field', name: item.name }));
      results = results.filter(({ name }) => name === trimmedContent);
      return [submit].concat(results as SuggestionOption[]);
    }
    return [submit];
  }
  return [];
};

interface SuggestionProps {
  selected: boolean;
  suggestion: SuggestionOption;
  onClick: () => void;
  onSelect: () => void;
  floating: boolean;
  prefixes: PrefixOption[];
  itemRef?: (el: HTMLDivElement | null) => void;
}

// Smart truncation that prioritizes showing the matched portion (marked with <mark> tags)
const truncateWithMatch = (html: string, maxLength: number): string => {
  const markStart = html.indexOf('<mark>');
  
  // If no match, use regular truncation
  if (markStart === -1) {
    return _.truncate(html, { length: maxLength, omission: '...' });
  }
  
  // Find the end of the mark tag
  const markEnd = html.indexOf('</mark>', markStart) + '</mark>'.length;
  
  // Strip HTML tags to calculate actual text length for the match
  const beforeMatch = html.substring(0, markStart);
  const matchContent = html.substring(markStart, markEnd);
  const afterMatch = html.substring(markEnd);
  
  // Calculate text length (without HTML tags)
  const stripTags = (str: string) => str.replace(/<[^>]*>/g, '');
  const matchTextLength = stripTags(matchContent).length;
  
  // Reserve space for the match and ellipsis
  const reservedLength = matchTextLength + 6; // 6 for "..." on each side
  const availableLength = maxLength - reservedLength;
  
  if (availableLength <= 0) {
    // If match is too long, just show the match with minimal context
    return '...' + matchContent + '...';
  }
  
  // Calculate how much context to show before and after
  const beforeTextLength = stripTags(beforeMatch).length;
  const afterTextLength = stripTags(afterMatch).length;
  
  let beforeContext = beforeMatch;
  let afterContext = afterMatch;
  
  // Distribute available space between before and after
  const halfAvailable = Math.floor(availableLength / 2);
  
  if (beforeTextLength > halfAvailable) {
    // Truncate before, keeping HTML tags intact
    const targetLength = halfAvailable;
    let currentLength = 0;
    let truncateAt = beforeMatch.length;
    
    for (let i = beforeMatch.length - 1; i >= 0; i--) {
      if (beforeMatch[i] === '>') {
        // Skip backwards through tag
        while (i >= 0 && beforeMatch[i] !== '<') i--;
        continue;
      }
      if (beforeMatch[i] !== '<') {
        currentLength++;
        if (currentLength >= targetLength) {
          truncateAt = i;
          break;
        }
      }
    }
    beforeContext = '...' + beforeMatch.substring(truncateAt);
  }
  
  if (afterTextLength > halfAvailable) {
    // Truncate after, keeping HTML tags intact
    const targetLength = halfAvailable;
    let currentLength = 0;
    let truncateAt = 0;
    
    for (let i = 0; i < afterMatch.length; i++) {
      if (afterMatch[i] === '<') {
        // Skip through tag
        while (i < afterMatch.length && afterMatch[i] !== '>') i++;
        continue;
      }
      currentLength++;
      if (currentLength >= targetLength) {
        truncateAt = i + 1;
        break;
      }
    }
    afterContext = afterMatch.substring(0, truncateAt) + '...';
  }
  
  return beforeContext + matchContent + afterContext;
};

// Helper component to render submit suggestions with chips for type A
const SubmitSuggestionContent = ({ content, type, prefixes }: { content: string; type?: string, prefixes: PrefixOption[] }) => {
  if (type !== 'A') {
    return (
      <SuggestionContent>
        Search <i>{content}</i>
      </SuggestionContent>
    );
  }

  // Normalize the content first to ensure proper parsing
  const normalizedContent = parser.processRaw(prefixes, content);
  
  // Parse the content to identify prefix:value pairs
  const nodes = parser.parseText(prefixes, normalizedContent);
  
  if (nodes.length === 0) {
    return (
      <SuggestionContent>
        Search <i>{content}</i>
      </SuggestionContent>
    );
  }

  // Group prefix and value nodes together into chips
  const chips: React.ReactNode[] = [];
  let i = 0;
  
  while (i < nodes.length) {
    const node = nodes[i];
    const text = parser.extractText(normalizedContent, node);
    
    if (node.type === PrefixNode) {
      // Check if next node is a ValueNode to combine them
      const nextNode = nodes[i + 1];
      if (nextNode && nextNode.type === ValueNode) {
        const valueText = parser.extractText(normalizedContent, nextNode);
        // Remove quotes and trim the value
        const cleanValue = valueText.replace(/^"|"$/g, '').trim();
        chips.push(
          <PrefixValueChip key={i}>
            {text}{cleanValue}
          </PrefixValueChip>,
        );
        i += 2; // Skip both prefix and value
      } else {
        // Prefix without value
        chips.push(<PrefixValueChip key={i}>{text}</PrefixValueChip>);
        i += 1;
      }
    } else if (node.type === ValueNode) {
      // Standalone value (shouldn't happen in normal cases)
      const cleanText = text.replace(/^"|"$/g, '').trim();
      if (cleanText) {
        chips.push(<PlainText key={i}>{cleanText}</PlainText>);
      }
      i += 1;
    } else {
      // TextNode - render as regular text
      const trimmedText = text.trim();
      if (trimmedText) {
        chips.push(<PlainText key={i}>{trimmedText}</PlainText>);
      }
      i += 1;
    }
  }

  return <ChipContainer>{chips}</ChipContainer>;
};

const Suggestion = ({ suggestion, selected, onClick, onSelect, floating, prefixes, itemRef }: SuggestionProps) => {
  if (suggestion.type === 'ticket') {
    const { ticket } = suggestion;
    const activity = ticket.activity;
    
    // Generate message prefix with sender name if available
    let prefixElement: React.ReactNode = null;
    
    if (activity?.sender_name) {
      prefixElement = <MessagePrefix>{activity.sender_name.trim()}: </MessagePrefix>;
    }
    
    // Replace <em> tags with <mark> tags to highlight search matches (same as TicketCardHeader)
    const messageText = activity?.message
      ? truncateWithMatch(
        activity.message.replaceAll('<em>', '<mark>').replaceAll('</em>', '</mark>'),
        90,
      )
      : '';

    return (
      <SearchResultItem
        ref={itemRef}
        $selected={selected}
        onClick={onClick}
        onMouseEnter={onSelect}
      >
        <SearchResultLeft>
          <SearchResultName>
            {ticket.contact.display_name || ticket.contact.identifier}
          </SearchResultName>
          {ticket.contact.company && (
            <SearchResultCompany>
              {_.truncate(ticket.contact.company, { length: 55, omission: '...' })}
            </SearchResultCompany>
          )}
          {messageText && (
            <SearchResultMessage>
              {prefixElement}
              <span dangerouslySetInnerHTML={{ __html: messageText }} />
            </SearchResultMessage>
          )}
        </SearchResultLeft>
        <SearchResultRight>
          <TicketUnaddressedIcon unaddressed={ticket.unaddressed} size={16} />
          <StatusBadge $status={ticket.status ?? ''}>{ticket.status ?? 'Unknown'}</StatusBadge>
        </SearchResultRight>
      </SearchResultItem>
    );
  }

  return (
    <DropdownItem
      ref={itemRef}
      $selected={selected}
      $layout={!floating && suggestion.type !== 'raw-value' ? 'vertical' : 'horizontal'}
      onClick={onClick}
      onMouseEnter={onSelect}
    >
      {suggestion.type === 'prefix' && <>
        <PrefixText>{suggestion.prefix}:</PrefixText>
        <DescriptionText>{suggestion.description}</DescriptionText>
      </>}

      {suggestion.type === 'prefixed-value' && <>
        <SuggestionContent>{`${suggestion.prefix}:"${suggestion.value}"`}</SuggestionContent>
        {suggestion.description && <DescriptionText>{suggestion.description}</DescriptionText>}
      </>}

      {suggestion.type === 'confirm-value' && <>
        <SuggestionContent>{suggestion.value}</SuggestionContent>
      </>}

      {suggestion.type === 'raw-value' && <>
        <SuggestionContent>{suggestion.value}</SuggestionContent>
        {suggestion.description && <DescriptionText>{suggestion.description}</DescriptionText>}
      </>}
      
      {suggestion.type === 'submit' && <>
        <SubmitSuggestionContent prefixes={prefixes} content={suggestion.content} type={(suggestion as any).renderType} />
      </>}

      {suggestion.type === 'user' && <>
        <SuggestionContent>{suggestion.name}</SuggestionContent>
      </>}

      {suggestion.type === 'contact-field' && <>
        <SuggestionContent>{suggestion.name}</SuggestionContent>
      </>}
    </DropdownItem>
  );
};

export const PrefixSearchInput: React.FC<PrefixSearchInputProps> = ({
  subdomain,
  value,
  onChange,
  onSubmit,
  searchAsYouType,
  onClear,
  onClose,
  maxInlineWidth,
  onFocus,
  onBlur,
  placeholder,
  className,
  type,
  disableFloating,
  dropdownMaxWidth,
  prefixes,
  children,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const wasFocused = usePreviousValue(isFocused);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [editorSelection, setEditorSelection] = useState<SearchPluginSelection>(null);
  const [editorContent, setEditorContent] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [inline, setInline] = useState(true);
  const [searchResults, setSearchResults] = useState<TicketSearchResult[]>([]);
  const [separators, setSeparators] = useState<number[]>([]);
  const [forceFloating, setForceFloating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>();
  const searchPluginRef = useRef<SearchPluginApi>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const dropdownItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const users = useSelector(_.partial(inboxUsersSelector, subdomain)) || {};
  const userSearch = useMemo(() => new Fuse(Object.values(users), { keys: ['name'], includeScore: true }), [users]);
  const [contactFieldVariables, setContactFieldVariables] = useState<ContactFieldVariable[]>([]);
  const contactFieldSearch = useMemo(() => new Fuse(contactFieldVariables, { keys: ['name'], includeScore: true }), [contactFieldVariables]);
  const prefixSearch = useMemo(() => new Fuse(prefixes, { keys: ['prefix', 'description'], includeScore: true, ignoreLocation: true }), [prefixes]);

  // Fetch required data on mount
  useEffect(() => {
    if (!subdomain) return;

    const fetchContactFields = async () => {
      try {
        const response = await avo.ajax<Array<{ name: string }>>({ 
          route: '/v1/variables', 
          params: { subdomain },
        });
        setContactFieldVariables(response.data);
      } catch (error) {
        console.error('Error fetching contact field variables:', error);
      }
    };
    
    const shouldFetchContactFields = prefixes.find((prefix) => prefix.dataType === 'contact-field');
    if (shouldFetchContactFields) fetchContactFields();
  }, [subdomain]);

  const floating = !!children || (!disableFloating && isFocused && (!inline || forceFloating));
  useEffect(() => { // put cursor at the end when focusing
    if (floating && !wasFocused) {
      searchPluginRef.current.focusEnd();
    }
  }, [floating]);

  // Detect when input content exceeds width threshold
  useEffect(() => {
    if (!maxInlineWidth) return;

    const inputElement = inputRef.current;
    if (!inputElement) return;

    const checkWidth = () => {
      const shouldBeInline = inputElement.offsetWidth <= maxInlineWidth;
      if (shouldBeInline !== inline) setInline(shouldBeInline);
    };
    checkWidth();
    const resizeObserver = new ResizeObserver(checkWidth);
    resizeObserver.observe(inputElement);
    return () => resizeObserver.disconnect();
  }, [inline, maxInlineWidth]);

  const handleContentChange = (updatedContent: string) => {
    setEditorContent(updatedContent);
    if (value !== updatedContent) onChange(updatedContent);
  };
  useEffect(() => {
    if (value !== editorContent) {
      searchPluginRef.current.setContent(value);
    }
  }, [value]);

  useDisableHotkeys(isFocused);

  // Debounced search for tickets
  const searchTickets = useMemo(
    () =>
      _.debounce(async (query: string) => {
        if (!query || query.trim().length < 2) {
          setSearchResults([]);
          return;
        }

        // Cancel any ongoing search
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        try {
          const response = await avo.ajax<{ tickets: TicketSearchResult[] }>({ route: '/v1/tickets/search_as_you_type', params: { subdomain, query } });
          setSearchResults(response.data.tickets);
        } catch (error: any) {
          if (error.name !== 'AbortError') {
            console.error('Search error:', error);
            setSearchResults([]);
          }
        }
      }, 300),
    [subdomain],
  );

  // Trigger search when content changes
  useEffect(() => {
    if (searchAsYouType === 'tickets') searchTickets(editorContent);
    return () => {
      searchTickets.cancel();
    };
  }, [searchAsYouType, editorContent, searchTickets]);

  // Check if any search results have matches that require more space to be visible
  useEffect(() => {
    if (disableFloating) return;
    if (!searchResults.length) {
      setForceFloating(false);
      return;
    }

    const INLINE_MAX_CHARS = 25; // Available characters in inline mode for sender + message
    
    const needsFloating = searchResults.some((ticket) => {
      const activity = ticket.activity;
      if (!activity?.message) return false;

      const message = activity.message;
      const markStart = message.indexOf('<em>');
      
      // If no match, no need to force floating
      if (markStart === -1) return false;

      // Calculate text length before the match (including sender name if present)
      const stripTags = (str: string) => str.replace(/<[^>]*>/g, '');
      const beforeMatch = message.substring(0, markStart);
      const senderLength = activity.sender_name ? activity.sender_name.length + 2 : 0; // +2 for ": "
      const beforeMatchLength = stripTags(beforeMatch).length + senderLength;

      // Find match content length
      const markEnd = message.indexOf('</em>', markStart);
      if (markEnd === -1) return false;
      
      const matchContent = message.substring(markStart + 4, markEnd); // +4 to skip '<em>'
      const matchLength = stripTags(matchContent).length;

      // If the match starts after available chars, or match + context exceeds limit, force floating
      const matchEnd = beforeMatchLength + matchLength;
      return beforeMatchLength > INLINE_MAX_CHARS || matchEnd > INLINE_MAX_CHARS;
    });

    setForceFloating(needsFloating);
  }, [disableFloating, searchResults]);

  const [dropdownOptions, setDropdownOptions] = useState(getDropdownOptions(editorSelection, editorContent, userSearch, contactFieldSearch, prefixes, prefixSearch, type));
  useEffect(() => {
    let updatedOptions = getDropdownOptions(editorSelection, editorContent, userSearch, contactFieldSearch, prefixes, prefixSearch, type);
    let updatedSeparators = [] as number[];
    
    // Add search results after suggestions if we have them
    if (searchResults.length > 0 && editorContent.trim().length >= 2) {
      updatedSeparators = [updatedOptions.length - 1];
      updatedOptions = [
        ...updatedOptions,
        ...searchResults.map((ticket) => ({ type: 'ticket', ticket } as SuggestionOption)),
      ];
    }
    
    if (JSON.stringify(updatedOptions) === JSON.stringify(dropdownOptions)) return;
    setDropdownOptions(updatedOptions);
    setSeparators(updatedSeparators);
    setSelectedIndex(0);
  }, [editorSelection, editorContent, userSearch, searchResults, type]);
  const selectedOption = dropdownOptions[selectedIndex];
  const state = useCurrentValue({ selectedOption, editorContent, lastOptionIx: dropdownOptions.length - 1 });

  const handleFocus = () => {
    setIsFocused(true);
    setSelectedIndex(0);
    onFocus?.();
  };

  const handleBlur: React.FocusEventHandler<HTMLDivElement> = () => {
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setIsFocused(false);
        onBlur?.();
      }
    }, 150);
  };

  const handleBackdropClick = useCallback(() => {
    if (children && onClose) {
      onClose();
    }
  }, [children, onClose]);

  const handleClear = useCallback(() => {
    onChange('');
    setSelectedIndex(0);
    onClear?.();
  }, [onChange]);

  const { navigate } = useNavigationActions();
  const params = useContext(AppNavigatorParamsContext);
  const handleDropdownItemClick = (suggestion: SuggestionOption) => {
    switch (suggestion.type) {
      case 'date':
      case 'submit':
        const trimmedContent = state.editorContent.trim();
        if (trimmedContent !== state.editorContent) {
          handleContentChange(trimmedContent);
        }
        searchPluginRef.current.blur();
        onSubmit(trimmedContent);
        break;
      case 'prefix':
        searchPluginRef.current.setCurrentNodeText(suggestion.prefix + ':');
        break;
      case 'prefixed-value':
        searchPluginRef.current.setCurrentNodeText(`${suggestion.prefix}:${suggestion.value}`);
        searchPluginRef.current.focusNextNode();
        break;
      case 'raw-value':
        searchPluginRef.current.setCurrentNodeText(suggestion.value);
        if (suggestion.finalize !== false) searchPluginRef.current.focusNextNode();
        break;
      case 'confirm-value':
        searchPluginRef.current.focusNextNode();
        break;
      case 'user':
        searchPluginRef.current.setCurrentNodeText(suggestion.name);
        searchPluginRef.current.focusNextNode();
        break;
      case 'contact-field':
        searchPluginRef.current.setCurrentNodeText(suggestion.name + '=');
        break;
      case 'ticket':
        // Navigate to the ticket (you might need to adjust this based on your routing)
        handleClear();
        const newParams = { ...params };
        if (suggestion.ticket.activity?.match) {
          newParams.matchId = suggestion.ticket.activity.event_id.toString();
          newParams.matchAt = suggestion.ticket.activity.sent_at.toString(); 
        }
        navigate({ path: `/accounts/${subdomain}/tickets/${suggestion.ticket.uuid}`, params: newParams });
        break;
      default:
        throw new Error(`Unknown suggestion: ${JSON.stringify(suggestion)}`);
    }
  };

  const handleDateChange = useCallback((date: Date) => {
    setSelectedDate(date);
    const dateValue = moment(date).format('YYYY-MM-DD');
    searchPluginRef.current.setCurrentNodeText(dateValue);
  }, []);

  const handleEnter = () => {
    handleDropdownItemClick(state.selectedOption);
  };

  const handleEscape = () => {
    searchPluginRef.current.blur();
    onClose?.();
  };

  const handleArrowDown = () => {
    setSelectedIndex((prevIndex) => {
      const newIndex = prevIndex >= state.lastOptionIx ? 0 : prevIndex + 1;
      // Scroll the new selected item into view
      setTimeout(() => {
        dropdownItemRefs.current[newIndex]?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest',
        });
      }, 0);
      return newIndex;
    });
  };

  const handleArrowUp = () => {
    setSelectedIndex((prevIndex) => {
      const newIndex = prevIndex <= 0 ? state.lastOptionIx : prevIndex - 1;
      // Scroll the new selected item into view
      setTimeout(() => {
        dropdownItemRefs.current[newIndex]?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest',
        });
      }, 0);
      return newIndex;
    });
  };

  const handleClickContainer = () => {
    if (!isFocused) searchPluginRef.current.focusEnd();
  };

  return (
    <>
      <Backdrop $visible={floating} onClick={handleBackdropClick} />
      <Container ref={containerRef} className={className} $floating={floating}>
        <InputContainer
          ref={inputContainerRef}
          $focused={isFocused}
          $floating={floating}
          $type={type}
          onClick={handleClickContainer}
        >
          <SearchIcon>
            <Search size="18px" strokeWidth="2" />
          </SearchIcon>

          <LexicalComposer initialConfig={{
            namespace: 'MyEditor',
            theme: {},
            nodes: searchNodes,
            onError: console.error,
          }}>
            <PlainTextPlugin
              contentEditable={
                <ContentEditable
                  ref={inputRef}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  spellCheck={false}
                />
              }
              placeholder={placeholder && <PlaceholderText>{placeholder}</PlaceholderText>}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <SearchPlugin
              ref={searchPluginRef}
              prefixes={prefixes}
              onUpdateSelection={setEditorSelection}
              onUpdateContent={handleContentChange}
              onPressEnter={handleEnter}
              onPressDown={handleArrowDown}
              onPressUp={handleArrowUp}
              onPressEscape={handleEscape}
            />
            <HistoryPlugin />
          </LexicalComposer>

          {value && (
            <ClearButton onClick={handleClear}>
              <X size="18px" strokeWidth="2" />
            </ClearButton>
          )}
        </InputContainer>
      
        {dropdownOptions[0]?.type === 'date' ? (
          <DatePickerContainer $visible={isFocused} $floating={floating}>
            <Calendar
              date={selectedDate}
              onChange={handleDateChange}
            />
          </DatePickerContainer>
        ) : (
          <DropdownWrapper $visible={isFocused && dropdownOptions.length > 0} $floating={floating} $maxWidth={dropdownMaxWidth}>
            <Dropdown $floating={floating} $hasHints={dropdownOptions.length > 0 && !children}>
              {dropdownOptions.map((option, index) => (
                <React.Fragment key={option.type === 'ticket' ? option.ticket.uuid : index.toString()}>
                  <Suggestion
                    selected={index === selectedIndex}
                    suggestion={option}
                    onClick={() => handleDropdownItemClick(option)}
                    onSelect={() => setSelectedIndex(index)}
                    floating={floating}
                    prefixes={prefixes}
                    itemRef={(el) => {
                      dropdownItemRefs.current[index] = el;
                    }}
                  />
                  {separators.includes(index) && <Separator />}
                </React.Fragment>
              ))}
            </Dropdown>
            {dropdownOptions.length > 0 && (
              <KeyboardHints $floating={floating}>
                <KeyboardHint>
                  <KeyboardKey><img width={12} height={12} src={arrowUpDown} /></KeyboardKey>
                  <KeyboardHintText>select</KeyboardHintText>
                </KeyboardHint>
                <KeyboardHint>
                  <KeyboardKey><img width={12} height={12} src={arrowLeftRight} /></KeyboardKey>
                  <KeyboardHintText>move between fields</KeyboardHintText>
                </KeyboardHint>
                <KeyboardHint>
                  <KeyboardKey><img width={12} height={12} src={arrowTurnDown} style={{ transform: 'rotate(90deg)' }} /></KeyboardKey>
                  <KeyboardHintText>search</KeyboardHintText>
                </KeyboardHint>
              </KeyboardHints>
            )}
          </DropdownWrapper>
        )}
        {children && (
          <ChildrenContainer>
            {children}
          </ChildrenContainer>
        )}
      </Container>
    </>
  );
};
